import React, { useMemo, useState } from "react";
import { X, Download, ArrowLeft, Loader2 } from "lucide-react";
import { L, tr } from "@/lib/i18n";
import { removeFromShortlist } from "@/lib/shortlist";
import { statusPill } from "./statusUtil";
import { visibleUnitFields, formatFieldValue, fieldLabel, standardDefinition } from "@/lib/unitFinder";

/**
 * ComparePanel — Phase A · Feature 3.
 *
 * Side-by-side comparison of shortlisted units selected from the Shortlist.
 * The row set is the union of canonical fields + the keys present on
 * at least one unit. Empty values render as a faint em-dash so the
 * column structure stays predictable on iPad landscape.
 *
 * Field resolution uses a synonym map so existing projects whose
 * inventory was authored with human-friendly field keys ("Area (sqm)",
 * "Price (USD)", "Floor") still light up the canonical rows. Custom
 * rows whose key is already consumed by a canonical row are dropped to
 * avoid the "duplicate row, one populated one empty" anti-pattern.
 */
export default function ComparePanel({
  slug, inventory, project, codes,
  onClose, onBack, onPickUnit, onLocate, onExportPdf,
  lang, defaultLang, fieldDefinitions = [], onCodesChange,
}) {
  const [localCodes, setLocalCodes] = useState(codes);
  const activeCodes = onCodesChange ? codes : localCodes;
  const setActiveCodes = next => onCodesChange ? onCodesChange(next) : setLocalCodes(next);
  const blockById = useMemo(() => Object.fromEntries(inventory.groups.map((g) => [g.id, g])), [inventory.groups]);
  const etapById = useMemo(() => Object.fromEntries(
    inventory.groups.filter((g) => g.group_type === "etap").map((g) => [g.id, g])), [inventory.groups]);

  const pathOf = (u) => {
    const b = blockById[u.group_id];
    const e = b && etapById[b.parent_group_id];
    return [e && (tr(e, "name", lang, defaultLang) || e.name),
            b && (tr(b, "name", lang, defaultLang) || b.name)].filter(Boolean).join(" › ");
  };

  const units = activeCodes
    .map((c) => inventory.entities.find((u) => u.code === c))
    .filter(Boolean);

  const definitions = useMemo(() => {
    const result = fieldDefinitions.slice();
    for (const key of ["code", "floor", "type", "net_area", "gross_area", "view", "price", "currency", "status"]) {
      if (!result.some(fd => fd.key === key)) result.push(standardDefinition(key));
    }
    return result;
  }, [fieldDefinitions]);
  const rowDefinitions = new Map();
  for (const unit of units) for (const row of visibleUnitFields(unit, definitions, "compare")) rowDefinitions.set(row.key, row.fd);
  const rows = [...rowDefinitions.values()].filter(fd => fd.key !== "price" || project.viewer_show_price !== false);

  const [pdfBusy, setPdfBusy] = useState(false);
  const onPdf = async () => {
    setPdfBusy(true);
    try {
      await onExportPdf(units.map((u) => u.code), "compare");
    } finally {
      setPdfBusy(false);
    }
  };

  const renderValue = (unit, fd) => {
    const value = formatFieldValue(unit, fd, lang, defaultLang, project);
    if (value == null || value === "") return <span className="text-white/25">—</span>;
    return <span className={fd.key === "status" ? statusPill(unit.status) : "text-white/90"}>{String(value)}</span>;
  };

  return (
    <div className="absolute inset-0 z-40 viewer-shell fade-in flex flex-col" data-testid="compare-panel">
      <div className="px-6 md:px-10 pt-6 pb-4 flex flex-wrap gap-3 items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="text-white/60 hover:text-white inline-flex items-center gap-1.5 text-xs uppercase tracking-wider" data-testid="compare-back">
            <ArrowLeft className="h-3.5 w-3.5" /> {L(lang, "shortlist")}
          </button>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/60">{tr(project, "name", lang, defaultLang) || project.name}</div>
            <div className="font-display text-3xl text-white tracking-tight mt-1">{L(lang, "compare")} · {units.length}</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {units.length > 0 && (
            <button
              onClick={onPdf}
              disabled={pdfBusy}
              className="pill-nav text-white text-xs uppercase tracking-wider px-4 py-2 rounded-full inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="compare-pdf-btn"
            >
              {pdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} {L(lang, "download_pdf")}
            </button>
          )}
          <button onClick={onClose} className="pill-nav text-white text-xs uppercase tracking-wider px-4 py-2 rounded-full inline-flex items-center gap-2" data-testid="compare-close">
            <X className="h-4 w-4" /> {L(lang, "back_to_tour")}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto scrollbar-thin">
        {units.length < 2 && (
          <div className="text-center py-24" data-testid="compare-empty">
            <div className="text-white/50 text-sm">{L(lang, "compare_min")}</div>
          </div>
        )}
        {units.length >= 2 && (
          <table className="w-full text-sm min-w-max" data-testid="compare-table">
            <thead>
              <tr>
                <th className="sticky left-0 bg-zinc-950 text-[10px] uppercase tracking-[0.25em] text-white/40 px-6 py-3 text-left w-44">&nbsp;</th>
                {units.map((u) => (
                  <th key={u.id} className="min-w-48 px-5 py-3 text-left border-b border-white/10">
                    <div className="font-display text-2xl text-white tracking-tight">{u.code}</div>
                    <div className="flex items-center justify-between mt-2 gap-2">
                      <button onClick={() => onPickUnit(u.id)} className="text-[10px] uppercase tracking-wider text-white/70 hover:text-white" data-testid={`compare-open-${u.code}`}>
                        {L(lang, "open")}
                      </button>
                      {onLocate && (
                        <button onClick={() => onLocate(u)} className="text-[10px] uppercase tracking-wider text-[#C4A47C] hover:text-white" data-testid={`compare-locate-${u.code}`}>
                          {L(lang, "locate")}
                        </button>
                      )}
                      <button
                        onClick={() => { removeFromShortlist(slug, u.code); setActiveCodes(activeCodes.filter(c => c !== u.code)); }}
                        className="text-[10px] uppercase tracking-wider text-white/40 hover:text-rose-300"
                        data-testid={`compare-remove-${u.code}`}
                      >
                        {L(lang, "remove")}
                      </button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr><td className="sticky start-0 bg-zinc-950 px-6 py-3">{L(lang, "phase")} / {L(lang, "block")}</td>{units.map(u => <td className="px-5 py-3" key={u.id}>{pathOf(u)}</td>)}</tr>
              {rows.map(fd => (
                <tr key={fd.key}>
                  <td className="sticky start-0 bg-zinc-950 px-6 py-3 text-[10px] uppercase tracking-[0.25em] text-white/40 border-b border-white/5">{fieldLabel(fd, lang, defaultLang)}</td>
                  {units.map(unit => <td key={unit.id} className="px-5 py-3 border-b border-white/5" data-testid={`compare-cell-${unit.code}-${fd.key}`}>{renderValue(unit, fd)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
